import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import useUser from 'src/hooks/use-user';
import useErnie from 'shared/hooks/use-ernie';
import { ERNIE_TYPES } from 'shared/constants';
import { SmallLoader } from 'shared/components';
import { Shield } from 'src/assets/shield';
import { BrandTintIconBadge } from 'src/components/modals/components/post-signup/brand-tint-icon-badge';
import { useCountdownTimer } from 'src/hooks/use-countdown-timer';
import { usePhoneVerification } from 'src/components/modals/phone-verification-nudge/use-phone-verification';
import { OtpEntry } from 'src/components/modals/phone-verification-nudge/components/otp-entry';
import type { OtpErrorKind } from 'src/components/modals/phone-verification-nudge/components/otp-entry';
import { ChangeNumber } from 'src/components/modals/phone-verification-nudge/components/change-number';
import type { ChangeNumberErrorKind } from 'src/components/modals/phone-verification-nudge/components/change-number';
import { BelongsToOther } from 'src/components/modals/phone-verification-nudge/components/belongs-to-other';
import {
  parseCheckStatus,
  parseSendStatus,
} from 'src/components/modals/phone-verification-nudge/phone-verification.types';
import { InlinePhoneVerificationPrompt } from './inline-phone-verification-prompt';

const RESEND_COOLDOWN_SECONDS = 30;

function NOOP(): void {
  // Intentional no-op for timer stop seam
}

export type InlinePhoneVerificationProps = {
  dispensaryId?: string | null;
  enterpriseId?: string | null;
  initialPhone?: string | null;
  onVerified: () => void;
  onClose: () => void;
  // verified_other: sign out and open login so the shopper can switch to the owning account.
  onLoginToOtherAccount: () => void;
};

type Step = 'belongs_to_other' | 'change_number' | 'loading' | 'otp' | 'prompt';
type ChangeNumberMode = 'add' | 'replace';

export const InlinePhoneVerification = ({
  dispensaryId,
  enterpriseId,
  initialPhone,
  onVerified,
  onClose,
  onLoginToOtherAccount,
}: InlinePhoneVerificationProps): JSX.Element => {
  const user = useUser();
  const showErnie = useErnie();
  const { fetchStatus, sendCode, verifyCode, changeNumber } = usePhoneVerification();

  const onVerifiedRef = useRef(onVerified);
  onVerifiedRef.current = onVerified;

  const hadInitialPhoneRef = useRef(Boolean(initialPhone && initialPhone.trim().length > 0));
  const [step, setStep] = useState<Step>('loading');
  const [changeNumberMode, setChangeNumberMode] = useState<ChangeNumberMode>(
    hadInitialPhoneRef.current ? 'replace' : 'add'
  );
  const [ownerEmailHint, setOwnerEmailHint] = useState<string | null>(null);
  const [phoneHint, setPhoneHint] = useState<string | null>(initialPhone ? initialPhone.slice(-4) : null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [promptError, setPromptError] = useState<string | null>(null);
  const [otpErrorKind, setOtpErrorKind] = useState<OtpErrorKind>(null);
  const [changeErrorKind, setChangeErrorKind] = useState<ChangeNumberErrorKind>(null);

  useEffect(() => {
    let isMounted = true;
    const showProfileDerivedStep = (): void => {
      if (hadInitialPhoneRef.current) {
        setStep('prompt');
      } else {
        setChangeNumberMode('add');
        setStep('change_number');
      }
    };

    void fetchStatus(dispensaryId, enterpriseId)
      .then((data) => {
        if (!isMounted) {
          return;
        }
        const statusPayload = data.phoneVerificationStatus;
        if (statusPayload?.status === 'verified_other') {
          setOwnerEmailHint(statusPayload.ownerEmailHint ?? null);
          setStep('belongs_to_other');
        } else if (statusPayload?.status === 'unverified' && statusPayload.hasPhone === false) {
          setChangeNumberMode('add');
          setStep('change_number');
        } else if (statusPayload?.status === 'unverified' && statusPayload.hasPhone) {
          setStep('prompt');
        } else if (statusPayload?.status === 'verified_self') {
          onVerifiedRef.current();
        } else {
          showProfileDerivedStep();
        }
      })
      .catch(() => {
        if (isMounted) {
          showProfileDerivedStep();
        }
      });

    return () => {
      isMounted = false;
    };
  }, [dispensaryId, enterpriseId, fetchStatus]);

  const { secondsLeft: resendSecondsLeft, startTimer: startResendCooldown } = useCountdownTimer({
    initialSeconds: RESEND_COOLDOWN_SECONDS,
    onTimerStop: NOOP,
  });

  // True only while the last sent code can still verify. secondsLeft starts at initialSeconds
  // before any send, so the cooldown alone can't answer this.
  const hasUsableCodeRef = useRef(false);
  // Screen that change-number's Back returns to; null falls back to prompt/close.
  const changeNumberReturnStepRef = useRef<Step | null>(null);
  const openChangeNumber = useCallback((returnStep: Step | null): void => {
    changeNumberReturnStepRef.current = returnStep;
    setChangeErrorKind(null);
    setChangeNumberMode('replace');
    setStep('change_number');
  }, []);
  const markCodeSent = useCallback((): void => {
    hasUsableCodeRef.current = true;
    startResendCooldown();
  }, [startResendCooldown]);

  const handleSendCode = useCallback(async (): Promise<void> => {
    // Back → Send during the cooldown reuses the outstanding code instead of sending another SMS.
    if (hasUsableCodeRef.current && resendSecondsLeft > 0) {
      setPromptError(null);
      setOtpErrorKind(null);
      setStep('otp');
      return;
    }
    setIsSubmitting(true);
    setPromptError(null);
    try {
      const result = await sendCode(dispensaryId, enterpriseId);
      const payload = result.sendPhoneVerificationCode;
      const sendStatus = parseSendStatus(payload?.status);

      if (sendStatus === 'sent') {
        if (payload?.phoneHint) {
          setPhoneHint(payload.phoneHint);
        }
        markCodeSent();
        setStep('otp');
      } else if (sendStatus === 'quarantined' || sendStatus === 'not_verifiable') {
        changeNumberReturnStepRef.current = null;
        setChangeNumberMode('replace');
        setStep('change_number');
        setChangeErrorKind('invalid_number');
      } else if (payload?.errorCode === 'throttled' || payload?.errorCode === 'rate_limited') {
        setPromptError("We've sent a lot of codes to this number recently. Please try again later.");
      } else if (payload?.errorCode === 'invalid_number' || payload?.errorCode === 'undeliverable') {
        changeNumberReturnStepRef.current = null;
        setChangeNumberMode('replace');
        setStep('change_number');
        setChangeErrorKind('invalid_number');
      } else {
        setPromptError('Something went wrong sending the code. Please try again.');
      }
    } catch {
      setPromptError('Something went wrong. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [dispensaryId, enterpriseId, markCodeSent, resendSecondsLeft, sendCode]);

  const handleResend = useCallback(async (): Promise<void> => {
    if (resendSecondsLeft > 0 || isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    setOtpErrorKind(null);
    try {
      const result = await sendCode(dispensaryId, enterpriseId);
      const payload = result.sendPhoneVerificationCode;
      const sendStatus = parseSendStatus(payload?.status);

      if (sendStatus === 'sent') {
        if (payload?.phoneHint) {
          setPhoneHint(payload.phoneHint);
        }
        markCodeSent();
      } else if (payload?.errorCode === 'throttled' || payload?.errorCode === 'rate_limited') {
        setOtpErrorKind('throttled');
      } else {
        setOtpErrorKind('generic');
      }
    } catch {
      setOtpErrorKind('generic');
    } finally {
      setIsSubmitting(false);
    }
  }, [dispensaryId, enterpriseId, isSubmitting, markCodeSent, resendSecondsLeft, sendCode]);

  const handleVerifyOtp = useCallback(
    async (code: string): Promise<void> => {
      setIsSubmitting(true);
      setOtpErrorKind(null);
      try {
        const result = await verifyCode(code, dispensaryId, enterpriseId);
        const checkStatus = parseCheckStatus(result.verifyPhoneVerificationCode?.status);
        // Only a mistyped code leaves the outstanding code usable.
        if (checkStatus !== 'code_invalid') {
          hasUsableCodeRef.current = false;
        }

        switch (checkStatus) {
          case 'verified':
            showErnie('Your phone number has been verified.', ERNIE_TYPES.SUCCESS);
            user.refetch();
            onVerifiedRef.current();
            break;
          case 'code_invalid':
            setOtpErrorKind('invalid');
            break;
          case 'code_expired':
            setOtpErrorKind('expired');
            break;
          case 'max_attempts':
            setOtpErrorKind('max_attempts');
            break;
          case 'conflict':
            setOtpErrorKind('conflict');
            break;
          case 'not_verifiable':
            setOtpErrorKind('generic');
            changeNumberReturnStepRef.current = null;
            setChangeNumberMode('replace');
            setStep('change_number');
            break;
          case 'disabled':
          case null:
          default:
            setOtpErrorKind('generic');
            break;
        }
      } catch {
        setOtpErrorKind('generic');
      } finally {
        setIsSubmitting(false);
      }
    },
    [dispensaryId, enterpriseId, showErnie, user, verifyCode]
  );

  const handleChangeNumberSubmit = useCallback(
    async (phone: string): Promise<void> => {
      setIsSubmitting(true);
      setChangeErrorKind(null);
      // The number on file may change server-side; the old code is no longer trusted.
      hasUsableCodeRef.current = false;
      try {
        const result = await changeNumber(phone, dispensaryId, enterpriseId);
        const payload = result.changePhoneVerificationNumber;
        const sendStatus = parseSendStatus(payload?.status);

        if (sendStatus === 'sent') {
          user.refetch();
          setPhoneHint(payload?.phoneHint ?? phone.slice(-4));
          markCodeSent();
          setStep('otp');
        } else if (sendStatus === 'quarantined' || sendStatus === 'not_verifiable') {
          setChangeErrorKind('invalid_number');
        } else if (payload?.errorCode === 'rate_limited' || payload?.errorCode === 'throttled') {
          setChangeErrorKind('throttled');
        } else if (payload?.errorCode === 'invalid_number' || payload?.errorCode === 'undeliverable') {
          setChangeErrorKind('invalid_number');
        } else {
          setChangeErrorKind('generic');
        }
      } catch {
        setChangeErrorKind('generic');
      } finally {
        setIsSubmitting(false);
      }
    },
    [changeNumber, dispensaryId, enterpriseId, markCodeSent, user]
  );

  const handleChangeNumberBack = useCallback((): void => {
    if (changeNumberReturnStepRef.current) {
      setStep(changeNumberReturnStepRef.current);
    } else if (hadInitialPhoneRef.current) {
      setStep('prompt');
    } else {
      onClose();
    }
  }, [onClose]);

  return (
    <Container data-testid='inline-phone-verification'>
      {step === 'loading' && (
        <LoadingContainer data-testid='inline-phone-verification-loading'>
          <SmallLoader />
        </LoadingContainer>
      )}

      {step === 'belongs_to_other' && (
        <BelongsToOther
          ownerEmailHint={ownerEmailHint}
          onLoginToOtherAccount={onLoginToOtherAccount}
          onChangePhone={() => {
            openChangeNumber('belongs_to_other');
          }}
          onUpdateLater={onClose}
        />
      )}

      {step === 'prompt' && (
        <InlinePhoneVerificationPrompt
          phoneHint={phoneHint}
          onVerify={() => {
            void handleSendCode();
          }}
          onChangeNumberClick={() => {
            openChangeNumber('prompt');
          }}
          onClose={onClose}
          isSubmitting={isSubmitting}
          errorMessage={promptError}
        />
      )}

      {step === 'change_number' && (
        <ChangeNumber
          title={changeNumberMode === 'add' ? 'Add a phone number' : 'Use a different phone number'}
          prompt={
            changeNumberMode === 'add'
              ? 'A verified phone number is required to join this loyalty program.'
              : 'Enter the phone number you want to use for this loyalty program.'
          }
          submitLabel='Send verification code'
          headerIcon={
            changeNumberMode === 'add' ? (
              <BrandTintIconBadge $size={64}>
                <Shield width={24} height={26} color='currentColor' />
              </BrandTintIconBadge>
            ) : undefined
          }
          showBackLink={changeNumberMode !== 'add'}
          onCancel={changeNumberMode === 'add' ? onClose : undefined}
          cancelLabel='Not right now'
          onBack={handleChangeNumberBack}
          onSubmit={(phone) => {
            void handleChangeNumberSubmit(phone);
          }}
          isSubmitting={isSubmitting}
          errorKind={changeErrorKind}
        />
      )}

      {step === 'otp' && (
        <OtpEntry
          phoneHint={phoneHint}
          description='Enter the code we sent to continue joining this loyalty program.'
          onChangeNumberClick={() => {
            openChangeNumber('otp');
          }}
          onSubmit={(code) => {
            void handleVerifyOtp(code);
          }}
          onBack={() => setStep('prompt')}
          onResend={() => {
            void handleResend();
          }}
          isSubmitting={isSubmitting}
          resendSecondsLeft={resendSecondsLeft}
          errorKind={otpErrorKind}
          showBackLink={hadInitialPhoneRef.current}
          showChangeNumberLink
        />
      )}
    </Container>
  );
};

const LoadingContainer = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  min-height: 240px;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;
